import * as React from "react";
import { useCallback, useContext, useMemo } from "react";
import * as Immutable from "immutable";
import { Formik, Form, Field } from "formik";
import isNil from "lodash/isNil";
import { useTranslation } from "react-i18next";
import { defaultCompare } from "logic/DefaultCompare";
import { Input, BootstrapModalWrapper, Modal } from "components/bootstrap";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import Select from "components/common/Select";
import { ConditionLabelMap, StringConditionLabelMap } from "views/logic/views/formatting/highlighting/HighlightingRule";
import HighlightingColorForm, { createNewColor } from "views/components/sidebar/highlighting/HighlightingColorForm";
import Series, { isFunction } from "views/logic/aggregationbuilder/Series";
import inferTypeForSeries from "views/logic/fieldtypes/InferTypeForSeries";
import { GradientColor, StaticColor } from "views/logic/views/formatting/highlighting/HighlightingColor";
import { ModalSubmit } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const _isRequired = (message) => (value) => {
  if (["", null, void 0].includes(value)) {
    return message;
  }
  return void 0;
};
const numberConditionOptions = Object.entries(ConditionLabelMap).map(([value, label]) => ({ value, label }));
const otherConditionOptions = Object.entries(StringConditionLabelMap).map(([value, label]) => ({ value, label }));
const fieldTypeFor = (fields, selectedField) => isFunction(selectedField) ? inferTypeForSeries(Series.forFunction(selectedField), fields) : fields.find((field) => field.name === selectedField);
const colorToObject = (color) => {
  const defaultColorType = "static";
  if ((color == null ? void 0 : color.type) === "static") {
    const { type, color: staticColor } = color;
    return {
      type,
      color: staticColor
    };
  }
  if ((color == null ? void 0 : color.type) === "gradient") {
    const { type, gradient, upper, lower } = color;
    return {
      type,
      gradient,
      upper,
      lower
    };
  }
  return createNewColor(defaultColorType);
};
const colorFromObject = (color) => {
  if ((color == null ? void 0 : color.type) === "static") {
    return StaticColor.create(color.color);
  }
  if ((color == null ? void 0 : color.type) === "gradient") {
    const { gradient, lower, upper } = color;
    return GradientColor.create(gradient, lower, upper);
  }
  return void 0;
};
const HighlightForm = ({ onClose, rule = void 0, onSubmit: onSubmitProp }) => {
  var _a;
  const { t } = useTranslation("search");
  const fieldTypes = useContext(FieldTypesContext);
  const sendTelemetry = useSendTelemetry();
  const fields = (fieldTypes == null ? void 0 : fieldTypes.all) ? fieldTypes.all : Immutable.List();
  const fieldOptions = useMemo(
    () => fields.map(({ name }) => ({ value: name, label: name })).sort((optA, optB) => defaultCompare(optA.label, optB.label)).toArray(),
    [fields]
  );
  const onSubmit = useCallback(
    ({ field, value, condition, color }) => {
      const newColor = colorFromObject(color);
      sendTelemetry(TELEMETRY_EVENT_TYPE[`SEARCH_SIDEBAR_HIGHLIGHT_${rule ? "UPDATED" : "CREATED"}`], {
        app_action_value: "search-sidebar-highlight"
      });
      return onSubmitProp(field, value, condition, newColor).then(onClose);
    },
    [onClose, onSubmitProp, rule, sendTelemetry]
  );
  const modalTitle = rule ? t("highlight_form.edit_title") : t("highlight_form.create_title");
  const submitButtonText = rule ? t("highlight_form.update_rule") : t("highlight_form.create_rule");
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      onSubmit,
      validateOnMount: true,
      initialValues: {
        field: rule == null ? void 0 : rule.field,
        value: isNil(rule == null ? void 0 : rule.value) ? "" : String(rule == null ? void 0 : rule.value),
        condition: (_a = rule == null ? void 0 : rule.condition) != null ? _a : "equal",
        color: colorToObject(rule == null ? void 0 : rule.color)
      }
    },
    ({ isValid, values: { field: selectedField } }) => {
      var _a2, _b;
      const selectedFieldType = fieldTypeFor(fields, selectedField);
      const isNumeric = (_b = (_a2 = selectedFieldType == null ? void 0 : selectedFieldType.type) == null ? void 0 : _a2.isNumeric()) != null ? _b : false;
      return /* @__PURE__ */ React.createElement(BootstrapModalWrapper, { showModal: true, onHide: onClose }, /* @__PURE__ */ React.createElement(Form, { className: "form", "data-testid": `${rule ? "Edit" : "Create"}-highlighting-rule-dialog` }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, modalTitle)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Field, { name: "field", validate: _isRequired(t("highlight_form.field_required")) }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(Input, { id: "field_type_controls", error: meta == null ? void 0 : meta.error, label: t("highlight_form.field_label") }, /* @__PURE__ */ React.createElement(
        Select,
        {
          inputId: "field-select",
          onChange: (newValue) => onChange({ target: { name, value: newValue } }),
          options: fieldOptions,
          allowCreate: true,
          value,
          placeholder: t("highlight_form.field_placeholder")
        }
      ))), /* @__PURE__ */ React.createElement(Field, { name: "condition", validate: _isRequired(t("highlight_form.condition_required")) }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(Input, { id: "condition-controls", error: meta == null ? void 0 : meta.error, label: t("highlight_form.condition_label") }, /* @__PURE__ */ React.createElement(
        Select,
        {
          inputId: "condition-select",
          onChange: (newValue) => onChange({ target: { name, value: newValue } }),
          options: isNumeric ? numberConditionOptions : otherConditionOptions,
          value,
          placeholder: t("highlight_form.condition_placeholder")
        }
      ))), /* @__PURE__ */ React.createElement(Field, { name: "value", validate: _isRequired(t("highlight_form.value_required")) }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          id: name,
          type: "text",
          error: meta == null ? void 0 : meta.error,
          onChange,
          value: value != null ? value : "",
          label: t("highlight_form.value_label")
        }
      )), /* @__PURE__ */ React.createElement(HighlightingColorForm, { field: selectedFieldType })), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(ModalSubmit, { onCancel: onClose, disabledSubmit: !isValid, submitButtonText }))));
    }
  );
};
export default HighlightForm;
